'use client';

/**
 * Approval preview for ticket writes (`bulk_update_tickets`, `update_ticket`).
 *
 * WHY: the generic approval card renders the raw tool input. For a bulk ticket
 * update that is an array of up to 50 UUIDs, so the user is asked to approve
 * closing "these tickets" without being shown which tickets. The chat text
 * above the card is not a substitute — the model may have listed only the first
 * page of results while the tool call carries the whole set (the reported case:
 * 8 tickets listed, 14 closed).
 *
 * This renders the resolved rows the tool will actually act on, plus the field
 * changes being applied to every one of them.
 */

export interface TicketPreviewRow {
  id: string;
  ticket_number?: number | null;
  title?: string | null;
  status?: string | null;
  priority?: string | null;
  company_name?: string | null;
}

export interface TicketBulkPreview {
  tickets: TicketPreviewRow[];
  requested: number;
  missing?: string[];
}

/** Tool-input keys that are targeting, not a field change. */
const NON_FIELD_KEYS = new Set(['ticket_ids', 'ticket_id', 'id']);

function humanizeValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.replace(/_/g, ' ');
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

export interface TicketBulkApprovalPreviewProps {
  preview: TicketBulkPreview;
  toolInput: unknown;
}

export function TicketBulkApprovalPreview({
  preview,
  toolInput,
}: TicketBulkApprovalPreviewProps) {
  const input = (toolInput ?? {}) as Record<string, unknown>;
  const changes = Object.entries(input).filter(
    ([key, value]) => !NON_FIELD_KEYS.has(key) && value !== undefined
  );

  const { tickets, requested, missing } = preview;

  return (
    <div className="mb-3 text-xs">
      <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
        {requested === 1
          ? 'This ticket will change:'
          : `These ${requested} tickets will change:`}
      </p>

      <div className="max-h-56 overflow-y-auto rounded border border-yellow-100 dark:border-yellow-800 bg-white dark:bg-slate-900">
        <ul className="divide-y divide-yellow-100 dark:divide-yellow-800">
          {tickets.map((ticket) => (
            <li key={ticket.id} className="px-2 py-1.5">
              <span className="font-mono text-slate-500 dark:text-slate-400">
                #{ticket.ticket_number ?? '—'}
              </span>{' '}
              <span className="text-slate-900 dark:text-slate-100">
                {ticket.title || 'Untitled ticket'}
              </span>
              <span className="block text-slate-500 dark:text-slate-400">
                {humanizeValue(ticket.status)}
                {ticket.company_name ? ` · ${ticket.company_name}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {missing && missing.length > 0 && (
        <p className="mt-1 text-red-700 dark:text-red-300">
          {missing.length} of the {requested} ids could not be resolved and are not shown.
        </p>
      )}

      {changes.length > 0 && (
        <div className="mt-2">
          <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
            Applied to {tickets.length === 1 ? 'it' : 'every one of them'}:
          </p>
          <ul className="space-y-0.5">
            {changes.map(([key, value]) => (
              <li key={key} className="text-slate-700 dark:text-slate-300">
                <span className="text-slate-500 dark:text-slate-400">
                  {key.replace(/_/g, ' ')}:
                </span>{' '}
                <span className="font-medium">{humanizeValue(value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
